"use client";

import React, { useEffect, useCallback, useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TaskAISummaryProps } from "@/lib/types/ai";
import { useTaskSummaryState } from "@/lib/hooks/ai/useTaskSummaryState";
import { useTaskSummaryGeneration } from "@/lib/hooks/ai/useTaskSummaryGeneration";
import { generateTaskSummaryCacheKey } from "@/lib/utils/ai/cache-key-generator";
import {
  getStatusMessage,
  getElapsedSeconds,
  getInitialStatusMessage,
} from "@/lib/utils/ai/status-messages";
import { TaskAISummaryHeader } from "./TaskAISummaryHeader";
import { TaskAISummarySkeleton } from "./TaskAISummarySkeleton";
import { TaskAISummaryContent } from "./TaskAISummaryContent";
import { TaskAISummaryCompact } from "./TaskAISummaryCompact";

/**
 * Task AI Summary Component
 *
 * Provides AI-powered analysis of task details and notes with streaming support.
 * Supports both full and compact layouts with collapsible content display.
 */
export function TaskAISummary({
  taskId,
  taskTitle,
  taskDescription,
  taskNotes,
  autoGenerate = false,
  showMetadata = true,
  showRefreshButton = true,
  showClearButton = false,
  compact = false,
  minimalProminence = false,
  className,
  onSummaryGenerated,
  onError,
}: TaskAISummaryProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [state, actions] = useTaskSummaryState();
  const { generateSummary: generateSummaryFn } = useTaskSummaryGeneration({
    onContentUpdate: actions.setDisplayedContent,
    onComplete: actions.setSummary,
    onError: (error) => {
      actions.setError(error);
      onError?.(error);
    },
  });

  // Generate cache key for this task summary
  const cacheKey = useMemo(() => {
    return generateTaskSummaryCacheKey(taskId, taskNotes.length);
  }, [taskId, taskNotes.length]);

  // Progressive status messages during generation
  useEffect(() => {
    if (state.isLoading || state.isStreaming) {
      if (!state.generationStartTime) {
        actions.setGenerationStartTime(Date.now());
        actions.setStatusMessage(getInitialStatusMessage());
      } else {
        const elapsed = getElapsedSeconds(state.generationStartTime);
        actions.setStatusMessage(getStatusMessage(elapsed));
      }
    } else if (state.summary?.content && !state.isLoading && !state.isStreaming) {
      actions.setStatusMessage("");
      actions.setGenerationStartTime(null);
    }
  }, [state.isLoading, state.isStreaming, state.generationStartTime, state.summary?.content]);

  // Handle streaming content completion
  useEffect(() => {
    if (state.summary?.content) {
      if (!state.isStreaming) {
        if (state.displayedContent !== state.summary.content) {
          actions.setDisplayedContent(state.summary.content);
          onSummaryGenerated?.(state.summary.content);
          actions.setIsExpanded(false);
        }
      }
    } else {
      actions.setDisplayedContent("");
    }
  }, [state.summary?.content, state.isStreaming, state.displayedContent, onSummaryGenerated]);

  const generateSummary = useCallback(async () => {
    if (state.isLoading) return;

    actions.setIsLoading(true);
    actions.setIsStreaming(true);
    actions.setError(null);

    try {
      const taskData = {
        taskId,
        taskTitle,
        taskDescription,
        taskNotes,
      };

      await generateSummaryFn(
        taskData,
        cacheKey,
        () => actions.setIsStreaming(true),
        () => actions.setIsStreaming(false)
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to generate summary";
      actions.setError(errorMessage);
    } finally {
      actions.setIsLoading(false);
      actions.setIsStreaming(false);
    }
  }, [state.isLoading, taskId, taskTitle, taskDescription, taskNotes, cacheKey, generateSummaryFn, actions]);

  const handleRefresh = useCallback(async () => {
    actions.setSummary(null);
    actions.setDisplayedContent("");
    actions.setError(null);
    await generateSummary();
  }, [generateSummary, actions]);

  const handleClear = useCallback(() => {
    actions.reset();
  }, [actions]);

  const handleMinimalPrimaryClick = useCallback(async () => {
    if (!panelOpen) {
      setPanelOpen(true);
    }
    // One-click behavior for minimized mode:
    // expand and run generation if we don't already have a summary.
    if (!state.summary?.content && !state.isLoading) {
      await generateSummary();
    }
  }, [panelOpen, state.summary?.content, state.isLoading, generateSummary]);

  // Auto-generate on mount if requested (minimal prominence: only after panel is open)
  useEffect(() => {
    if (!autoGenerate || state.summary || state.isLoading || state.error) return;
    if (minimalProminence && !panelOpen) return;
    generateSummary().catch((err) => {
      console.error("Auto-generate error:", err);
      onError?.(err instanceof Error ? err.message : "Failed to generate summary");
    });
  }, [
    autoGenerate,
    minimalProminence,
    panelOpen,
    state.summary,
    state.isLoading,
    state.error,
    generateSummary,
    onError,
  ]);

  if (compact) {
    return (
      <TaskAISummaryCompact
        summary={state.summary}
        isLoading={state.isLoading}
        isStreaming={state.isStreaming}
        error={state.error}
        displayedContent={state.displayedContent}
        isExpanded={state.isExpanded}
        statusMessage={state.statusMessage}
        showRefreshButton={showRefreshButton}
        showMetadata={showMetadata}
        taskNotes={taskNotes}
        onRefresh={handleRefresh}
        onGenerate={generateSummary}
        onClear={handleClear}
        onToggleExpanded={actions.toggleExpanded}
        className={className}
      />
    );
  }

  const fullCard = (
    <Card className={cn("w-full", minimalProminence ? undefined : className)}>
      <CardHeader className="pb-3 px-4 md:px-6">
        <TaskAISummaryHeader
          isLoading={state.isLoading}
          isStreaming={state.isStreaming}
          showRefreshButton={showRefreshButton}
          showClearButton={showClearButton}
          hasSummary={!!state.summary}
          onRefresh={handleRefresh}
          onGenerate={generateSummary}
          onClear={handleClear}
          summaryMarkdown={state.summary?.content || state.displayedContent}
          taskId={taskId}
          canExport={Boolean(
            (state.summary?.content || state.displayedContent) &&
              !state.isLoading &&
              !state.isStreaming
          )}
        />
      </CardHeader>

      <CardContent className="space-y-4 px-4 md:px-6">
        {state.error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
        )}

        {(state.isLoading || state.isStreaming) && (
          <TaskAISummarySkeleton statusMessage={state.statusMessage} />
        )}

        {state.displayedContent && !state.isLoading && !state.isStreaming && (
          <TaskAISummaryContent
            displayedContent={state.displayedContent}
            isExpanded={state.isExpanded}
            onToggleExpanded={actions.toggleExpanded}
            summary={state.summary}
            showMetadata={showMetadata}
          />
        )}

        {!state.summary && !state.isLoading && !state.error && (
          <div className="text-center py-6 md:py-8">
            <Sparkles className="h-10 w-10 md:h-12 md:w-12 text-muted-foreground mx-auto mb-3 md:mb-4" />
            <p className="text-sm md:text-base text-muted-foreground mb-3 md:mb-4">
              {taskNotes.length > 0
                ? "Generate AI insights from task details and notes."
                : "Generate AI insights from task details."}
            </p>
            <Button
              onClick={generateSummary}
              disabled={state.isLoading}
              className="h-11 sm:h-9 w-full sm:w-auto"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate AI Summary
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (minimalProminence) {
    const hasReadySummary = Boolean(state.summary?.content);
    return (
      <>
        <div className={cn("flex flex-wrap items-center gap-2", className)}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 px-2.5 text-xs font-normal"
            onClick={() => {
              if (panelOpen) {
                setPanelOpen(false);
                return;
              }
              void handleMinimalPrimaryClick();
            }}
          >
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            {panelOpen ? "Hide AI summary" : "AI summary"}
            {hasReadySummary ? (
              <span className="text-muted-foreground font-normal">· Ready</span>
            ) : null}
          </Button>
        </div>
        {panelOpen ? fullCard : null}
      </>
    );
  }

  return fullCard;
}
